import { useTranslation } from "react-i18next";
import { ViewAction } from "@/components/table/actions";

import { Button } from "@/components/ui/button";
import { Download, FileText } from "lucide-react";
import { isImageUrl, useImagePreview } from "@/context/ImagePreview";
import { useFileDownloader } from "@/hooks/useFileDownloader";

interface DocumentSidePreviewProps {
  label?: string;
  url: string;
  onDownload?: (url: string) => void;
  onPreview?: (url: string) => void;
}

const DocumentSidePreview = ({
  label,
  url,
  onDownload,
  onPreview,
}: DocumentSidePreviewProps) => {
  const { t } = useTranslation("common");
  const openPreview = useImagePreview();
  const { downloadFile } = useFileDownloader();
  const isImage = isImageUrl(url);
  const fileName = decodeURIComponent(url.split("/").pop()?.split("?")[0] ?? "");
  const canPreview = isImage;

  const handlePreview = (u: string) => {
    if (onPreview) onPreview(u);
    else openPreview([u]);
  };

  const handleDownload = (u: string) => {
    if (onDownload) onDownload(u);
    else downloadFile(u);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
      )}

      <div
        className={`relative flex items-center bg-background rounded-sm p-1.5 gap-2 group transition-colors duration-150 ${canPreview ? "cursor-pointer hover:bg-primary/10" : ""}`}
        onClick={() => canPreview && handlePreview(url)}
      >
        {/* Thumbnail / icon */}
        <div className="relative w-11 h-11 rounded-sm bg-white shrink-0 flex items-center justify-center overflow-hidden">
          {isImage ? (
            <img src={url} className="w-full h-full object-cover" alt={fileName} />
          ) : (
            <FileText className="w-5 h-5 text-primary" />
          )}
        </div>

        {/* Filename */}
        <div className="grow min-w-0">
          <p className="text-xs font-medium truncate text-foreground" title={fileName}>
            {fileName}
          </p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mt-0.5">
            {url.split(".").pop()?.split("?")[0] ??
              t("common:shared_ui.file_fallback")}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {canPreview && (
            <ViewAction onClick={(e) => { e.stopPropagation(); handlePreview(url); }} tooltip={t("shared_ui.preview")} className="w-8 h-8 text-muted-foreground hover:text-primary hover:bg-white/60" />
          )}
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 text-muted-foreground hover:text-primary hover:bg-white/60 shrink-0"
            onClick={(e) => { e.stopPropagation(); handleDownload(url); }}
            aria-label={t("shared_ui.download")}
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={t("shared_ui.download")}
          >
            <Download className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentSidePreview;
